// ── Adapter Supabase do ICadenceRepository ────────────────────────
// Implementa a porta do módulo Activity (Cadence) usando o cliente Supabase.
// É a ÚNICA camada que conhece a RPC/tabelas do banco para cadência.

import { getSupabase, type TypedSupabaseClient } from '@infra/supabase/client';
import type { CadenceStep } from '@models';
import type { ICadenceRepository, NextStage, CardStageInfo } from '@modules/activity';

const requireSupabase = (): TypedSupabaseClient => {
  const supabase = getSupabase();
  if (!supabase) throw new Error('Supabase nao inicializado.');
  return supabase;
};

interface CardWithStage {
  pipeline_id: string | null;
  stage_id: string | null;
  pipeline_stages: { id: string; name: string; order_index: number } | null;
}

export class SupabaseCadenceRepository implements ICadenceRepository {
  triggerStageCadence: ICadenceRepository['triggerStageCadence'] = async ({ cardId, stageId, assignedTo = null }) => {
    const supabase = requireSupabase();
    const { data, error } = await supabase.rpc('trigger_stage_cadence', {
      p_card_id:     cardId,
      p_stage_id:    stageId,
      p_assigned_to: assignedTo ?? undefined,
    });
    if (error) {
      console.error('[cadence] trigger_stage_cadence falhou:', error);
      throw error;
    }
    return data;
  };

  getCardStageInfo: ICadenceRepository['getCardStageInfo'] = async (cardId) => {
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('cards')
      .select(`
        pipeline_id,
        stage_id,
        pipeline_stages!cards_stage_id_fkey ( id, name, order_index )
      `)
      .eq('id', cardId)
      .single();
    const card = data as unknown as CardWithStage | null;
    if (error || !card) return null;
    return {
      pipeline_id:   card.pipeline_id,
      current_order: card.pipeline_stages?.order_index ?? -1,
    } as CardStageInfo;
  };

  getNextStage: ICadenceRepository['getNextStage'] = async (pipelineId, currentOrder) => {
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('pipeline_stages')
      .select('id, name, color, order_index')
      .eq('pipeline_id', pipelineId)
      .eq('ativo', true)
      .gt('order_index', currentOrder)
      .order('order_index', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (error) return null;
    return (data as unknown as NextStage) || null;
  };

  getPipelineCadence: ICadenceRepository['getPipelineCadence'] = async (pipelineId) => {
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('pipelines')
      .select('cadence')
      .eq('id', pipelineId)
      .single();
    if (error) throw error;
    return Array.isArray(data?.cadence) ? (data.cadence as CadenceStep[]) : [];
  };

  setPipelineCadence: ICadenceRepository['setPipelineCadence'] = async (pipelineId, cadence) => {
    const supabase = requireSupabase();
    const { error } = await supabase
      .from('pipelines')
      .update({ cadence: cadence as never })
      .eq('id', pipelineId);
    if (error) throw error;
  };
}
